import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { Search, FileDownload, Print } from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { getApiBaseUrl, getLogoUrl } from '../../../config/api';
import { notifyError } from '../../../utils/notify';
import { createAxiosConfig, getInstitutionId, exportToExcelWithBoldHeaders, formatMonthYear, parseMonthYear } from '../../../utils/feeUtils';

const API_URL = getApiBaseUrl();

const BankStatementReport = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [institution, setInstitution] = useState(null);
  const [user, setUser] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [institutionsMap, setInstitutionsMap] = useState({});
  const [filters, setFilters] = useState({
    filterMode: 'dateRange', // 'dateRange' (default) or 'month'
    monthYear: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    bankAccount: '',
    statusFilter: 'all'
  });

  useEffect(() => {
    const savedInstitution = localStorage.getItem('selectedInstitution');
    if (savedInstitution) {
      try {
        setInstitution(JSON.parse(savedInstitution));
      } catch (e) {
        console.error('Error parsing institution data');
      }
    }
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const u = JSON.parse(savedUser);
        setUser(u);
        const isSuperAdmin = u?.role === 'super_admin';
        const institutionId = getInstitutionId(u, isSuperAdmin);
        if (institutionId) {
          axios.get(`${API_URL}/institutions/${institutionId}`, createAxiosConfig())
            .then(res => { if (res.data?.data) setInstitution(res.data.data); })
            .catch(e => console.error('Error fetching institution details:', e));
        }
      } catch (e) {
        console.error('Error parsing user data');
      }
    }
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const isSuperAdmin = savedUser?.role === 'super_admin';
        const institutionId = getInstitutionId(savedUser, isSuperAdmin);
        
        const params = {};
        if (institutionId) {
          params.institution = institutionId;
        }

        const [bankRes, instRes] = await Promise.all([
          axios.get(`${API_URL}/bank-accounts`, createAxiosConfig({ params })),
          axios.get(`${API_URL}/institutions`, createAxiosConfig()).catch(() => ({ data: { data: [] } }))
        ]);

        setBankAccounts(bankRes.data.data || []);
        
        const instList = instRes.data.data || [];
        const map = {};
        instList.forEach(inst => {
          if (inst && inst._id) map[inst._id.toString()] = inst;
        });
        setInstitutionsMap(map);
      } catch (err) {
        console.error('Error fetching bank accounts/institutions:', err);
      }
    };
    fetchInitialData();
  }, []);

  const resolveBankAccount = (item) => {
    if (item.bankAccount) {
      const bId = item.bankAccount._id || item.bankAccount;
      const b = bankAccounts.find(x => x._id === bId);
      if (b) return b;
    }
    
    if (item.bankName) {
      const pName = item.bankName.toLowerCase().trim();
      let exact = bankAccounts.find(b => b.bankName.toLowerCase().trim() === pName);
      if (exact) return exact;
      
      let bestMatch = null, maxLen = 0;
      for (const b of bankAccounts) {
        const bName = b.bankName.toLowerCase().trim();
        if (bName.includes(pName) || pName.includes(bName)) {
          const matchLen = Math.min(bName.length, pName.length);
          if (matchLen > maxLen) {
            maxLen = matchLen;
            bestMatch = b;
          }
        }
      }
      if (bestMatch) return bestMatch;
    }
    
    if (item.remarks) {
      const rName = item.remarks.toLowerCase().trim();
      let bestMatch = null, maxLen = 0;
      for (const b of bankAccounts) {
        const bName = b.bankName.toLowerCase().trim();
        if (rName.includes(bName) && bName.length > maxLen) {
          maxLen = bName.length;
          bestMatch = b;
        }
      }
      if (bestMatch) return bestMatch;
    }
    return null;
  };

  const getBankShortName = (name) => {
    if (!name) return '';
    const clean = name.trim().toUpperCase();
    if (clean.includes('MEEZAN')) return 'MBL';
    if (clean.includes('HABIB') || clean.includes('HBL')) return 'HBL';
    if (clean.includes('UNITED') || clean.includes('UBL')) return 'UBL';
    if (clean.includes('NATIONAL') || clean.includes('NBP')) return 'NBP';
    if (clean.includes('ALLIED') || clean.includes('ABL')) return 'ABL';
    if (clean.includes('ASKARI') || clean.includes('AKBL')) return 'AKBL';
    if (clean.includes('ALFALAH') || clean.includes('BAFL')) return 'BAFL';
    if (clean.includes('FAYSAL') || clean.includes('FBL')) return 'FBL';
    if (clean.includes('PUNJAB') || clean.includes('BOP')) return 'BOP';
    if (clean.includes('MCB')) return 'MCB';
    if (clean.includes('JS BANK') || clean.includes('JSB')) return 'JSB';
    if (clean.includes('DUBAI') || clean.includes('DIB')) return 'DIB';
    if (clean.includes('STANDARD') || clean.includes('SCB')) return 'SCB';
    if (clean.includes('SONERI')) return 'SBL';
    if (clean.includes('ISLAMI')) return 'BIPL';

    const words = clean.split(/\s+/);
    if (words.length > 1) {
      return words.map(w => w[0]).join('');
    }
    return clean.substring(0, 4);
  };

  const getBankDisplay = (item) => {
    let matchedBank = resolveBankAccount(item);
    
    if (!matchedBank && filters.bankAccount) {
      matchedBank = bankAccounts.find(b => b._id === filters.bankAccount);
    }
    if (!matchedBank && bankAccounts.length === 1) {
      matchedBank = bankAccounts[0];
    }

    const rawBankName = matchedBank ? matchedBank.bankName : (item.bankName || '');
    const shortName = getBankShortName(rawBankName);
    
    const accNum = matchedBank ? matchedBank.accountNumber : '';
    const last3 = accNum && accNum.length >= 3 ? accNum.slice(-3) : accNum;

    if (shortName && last3) {
      return `${shortName}-${last3}`;
    }
    return shortName || last3 || item.bankName || '-';
  };

  const getCampusShortForm = (itemInst, globalInst, instMap = {}) => {
    let instObj = itemInst;

    if (typeof instObj === 'string') {
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(instObj);
      if (isObjectId) {
        if (instMap[instObj]) {
          instObj = instMap[instObj];
        } else {
          instObj = globalInst;
        }
      }
    }

    if (!instObj) instObj = globalInst;

    let rawName = '';
    if (typeof instObj === 'string') {
      if (/^[0-9a-fA-F]{24}$/.test(instObj)) {
        rawName = globalInst?.name || 'Taj';
      } else {
        rawName = instObj;
      }
    } else if (instObj && typeof instObj === 'object') {
      rawName = instObj.name || instObj.address?.city || globalInst?.name || 'Taj';
    }

    if (!rawName || /^[0-9a-fA-F]{24}$/.test(rawName)) {
      rawName = globalInst?.name || 'Taj';
    }

    let name = rawName.trim();

    // If contains hyphen like "TIGES - TAJ CAMPUS", take the portion after "-"
    if (name.includes('-')) {
      const parts = name.split('-');
      name = parts[parts.length - 1].trim();
    }

    // Remove trailing "Campus" or "CAMPUS" if present
    name = name.replace(/\b(campus|CAMPUS|Campus)\b/gi, '').trim();

    // Remove common organization prefixes if present
    name = name.replace(/^(SGC|TIGES|ERP)\s+/gi, '').trim();

    if (!name || /^[0-9a-fA-F]{24}$/.test(name)) {
      name = 'Taj';
    }

    // Capitalize neatly (e.g. "TAJ" -> "Taj", "MUZAFFARABAD" -> "Muzaffarabad")
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const handleFetchReport = async () => {
    try {
      setLoading(true);
      const config = createAxiosConfig();
      const isSuperAdmin = user?.role === 'super_admin';
      const institutionId = getInstitutionId(user, isSuperAdmin);

      let fromDate = null;
      let toDate = null;
      let targetMonth = null;
      let targetYear = null;

      if (filters.filterMode === 'dateRange') {
        if (!filters.dateFrom || !filters.dateTo) {
          notifyError('Please select both Date From and Date To');
          setLoading(false);
          return;
        }
        if (filters.dateFrom > filters.dateTo) {
          notifyError('Date From cannot be greater than Date To');
          setLoading(false);
          return;
        }
        fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
      } else {
        if (!filters.monthYear) {
          notifyError('Please select Fee Month & Year');
          setLoading(false);
          return;
        }
        const parsed = parseMonthYear(filters.monthYear);
        targetMonth = parsed.month;
        targetYear = parsed.year;
      }

      const params = {
        institution: institutionId
      };
      if (filters.filterMode === 'dateRange') {
        params.startDate = filters.dateFrom;
        params.endDate = filters.dateTo;
      }

      // Selected bank account object if any
      const selectedBankObj = bankAccounts.find(b => b._id === filters.bankAccount);
      const bankKey = selectedBankObj ? selectedBankObj.bankName.toLowerCase() : '';

      // Fetch Fee Payments and Suspense Entries in parallel
      const [paymentsRes, suspenseRes] = await Promise.all([
        axios.get(`${API_URL}/fees/payments`, { ...config, params }),
        axios.get(`${API_URL}/fees/suspense`, { ...config, params: { institution: institutionId } })
      ]);

      let rawPayments = paymentsRes.data.data || [];
      let rawSuspense = suspenseRes.data.data || [];

      // Filter Fee Payments by bank and completion status
      rawPayments = rawPayments.filter(p => p.status === 'completed');
      if (filters.bankAccount) {
        rawPayments = rawPayments.filter(p => {
          const resolved = resolveBankAccount(p);
          return resolved && resolved._id === filters.bankAccount;
        });
      } else {
        // When no bank is selected, show all bank-related payments (bank_transfer, online, cheque)
        rawPayments = rawPayments.filter(p =>
          ['bank_transfer', 'online', 'cheque'].includes(p.paymentMethod) || p.bankName
        );
      }

      // Filter Suspense Entries by date range or fee month and bank
      if (filters.filterMode === 'dateRange') {
        rawSuspense = rawSuspense.filter(s => {
          const pDate = new Date(s.paymentDate || s.createdAt);
          if (fromDate && pDate < fromDate) return false;
          if (toDate && pDate > toDate) return false;
          return true;
        });
      } else if (filters.filterMode === 'month') {
        rawSuspense = rawSuspense.filter(s => {
          const pDate = new Date(s.paymentDate || s.createdAt);
          return (pDate.getMonth() + 1) === targetMonth && pDate.getFullYear() === targetYear;
        });
      }

      if (filters.bankAccount) {
        rawSuspense = rawSuspense.filter(s => {
          const resolved = resolveBankAccount(s);
          return resolved && resolved._id === filters.bankAccount;
        });
      }

      // Normalize Fee Payments into transaction objects, grouped by transactionId+student
      // One bank transaction (same transactionId) covers multiple fee heads — merge them into one row
      const txnMap = {};
      rawPayments.forEach(p => {
        const studentName = p.studentName || p.student?.personalDetails?.name || `Receipt: ${p.receiptNumber || 'N/A'}`;
        // Group by transactionId if available, fallback to voucherNumber+student, then receiptNumber
        const groupKey = p.transactionId
          ? `${p.transactionId}__${studentName}`
          : p.voucherNumber
            ? `VCH__${p.voucherNumber}__${studentName}`
            : p._id;

        // Get fee month and sortable date value from studentFee.vouchers or payment
        let feeMonth = '';
        let monthSortVal = 0;
        if (p.studentFee && p.studentFee.vouchers && p.studentFee.vouchers.length > 0) {
          const v = p.studentFee.vouchers[0];
          if (v && v.month && v.year) {
            feeMonth = new Date(v.year, v.month - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
            monthSortVal = Number(v.year) * 100 + Number(v.month);
          }
        }
        if (!feeMonth && p.feeMonth && p.feeMonth !== 'N/A') {
          feeMonth = p.feeMonth;
        }

        if (txnMap[groupKey]) {
          txnMap[groupKey].amount += p.amount;
          // When consolidating multiple months/heads into one transaction, prefer the latest fee month
          if (monthSortVal > (txnMap[groupKey]._monthSortVal || 0)) {
            txnMap[groupKey].feeMonth = feeMonth;
            txnMap[groupKey]._monthSortVal = monthSortVal;
          } else if (!txnMap[groupKey].feeMonth && feeMonth) {
            txnMap[groupKey].feeMonth = feeMonth;
          }
          if (p.voucherNumber && (!txnMap[groupKey].voucherNumber || txnMap[groupKey].voucherNumber === '-')) {
            txnMap[groupKey].voucherNumber = p.voucherNumber;
          }
        } else {
          txnMap[groupKey] = {
            _id: p._id,
            _monthSortVal: monthSortVal,
            date: p.paymentDate || p.createdAt,
            amount: p.amount,
            transactionId: p.transactionId || '-',
            bankName: p.bankName || (selectedBankObj ? selectedBankObj.bankName : 'Bank'),
            bankAccount: p.bankAccount,
            status: 'reconciled',
            statusLabel: 'Reconciled',
            details: studentName,
            studentRoll: p.student?.rollNumber || '-',
            voucherNumber: p.voucherNumber || '-',
            feeMonth,
            campusCode: getCampusShortForm(p.institution || p.student?.institution, institution, institutionsMap)
          };
        }
      });
      const paymentTxns = Object.values(txnMap);

      // Normalize Suspense Entries into transaction objects
      const suspenseTxns = rawSuspense.map(s => ({
        _id: s._id,
        date: s.paymentDate || s.createdAt,
        amount: s.amount,
        transactionId: s.transactionId || '-',
        bankName: s.bankName || (selectedBankObj ? selectedBankObj.bankName : 'Bank'),
        status: s.status === 'reconciled' ? 'reconciled_suspense' : 'suspense',
        statusLabel: s.status === 'reconciled' ? 'Reconciled Suspense' : 'Suspense',
        details: s.remarks || 'Suspense Entry',
        studentRoll: '-',
        voucherNumber: '-',
        campusCode: getCampusShortForm(s.institution, institution, institutionsMap)
      }));

      // Filter payments by Month/Voucher when in month mode
      let filteredPaymentTxns = paymentTxns;
      if (filters.filterMode === 'month') {
        filteredPaymentTxns = paymentTxns.filter(t => {
          if (t._monthSortVal) {
            const tYear = Math.floor(t._monthSortVal / 100);
            const tMonth = t._monthSortVal % 100;
            return tYear === targetYear && tMonth === targetMonth;
          }
          if (t.date) {
            const d = new Date(t.date);
            return (d.getMonth() + 1) === targetMonth && d.getFullYear() === targetYear;
          }
          return false;
        });
      }

      // Combine and filter by status option
      let combined = [...filteredPaymentTxns, ...suspenseTxns];
      if (filters.statusFilter === 'reconciled') {
        combined = combined.filter(t => t.status === 'reconciled' || t.status === 'reconciled_suspense');
      } else if (filters.statusFilter === 'suspense') {
        combined = combined.filter(t => t.status === 'suspense');
      }

      // Sort by date descending
      combined.sort((a, b) => new Date(b.date) - new Date(a.date));

      setData(combined);
    } catch (err) {
      notifyError(err.response?.data?.message || 'Failed to fetch bank transactions report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    if (data.length === 0) return;

    const exportData = data.map((item, index) => ({
      'Sr #': index + 1,
      'Campus': item.campusCode || 'Taj',
      'Date': formatDate(item.date),
      'Transection Month': item.feeMonth || '-',
      'Amount': item.amount,
      'Transaction ID': item.transactionId,
      'Bank': getBankDisplay(item),
      'Status': item.statusLabel,
      'Details / Student': item.details
    }));

    const selectedBankObj = bankAccounts.find(b => b._id === filters.bankAccount);
    const bankTitle = selectedBankObj 
      ? `${selectedBankObj.bankName} (${selectedBankObj.accountNumber})` 
      : 'All Accounts';

    const isMonthMode = filters.filterMode === 'month';
    const monthLabel = isMonthMode ? formatMonthYear(...Object.values(parseMonthYear(filters.monthYear))) : '';
    const fileSuffix = isMonthMode ? filters.monthYear : `${filters.dateFrom}_to_${filters.dateTo}`;
    const exportMeta = isMonthMode ? {
      'Fee Month': monthLabel,
      'Bank Title': bankTitle
    } : {
      'From': formatDate(filters.dateFrom),
      'To': formatDate(filters.dateTo),
      'Bank Title': bankTitle
    };

    exportToExcelWithBoldHeaders(
      XLSX,
      exportData,
      'Bank Transactions Report',
      `Bank_Transactions_${fileSuffix}.xlsx`,
      exportMeta
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const totalAmount = data.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalReconciled = data
    .filter(item => item.status === 'reconciled' || item.status === 'reconciled_suspense')
    .reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalSuspense = data
    .filter(item => item.status === 'suspense')
    .reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <Box>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #report-print-area, #report-print-area * {
              visibility: visible;
            }
            #report-print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 0;
              margin: 0;
              color: black !important;
            }
            .no-print {
              display: none !important;
            }
            @page {
              size: landscape;
              margin: 10mm;
            }
            table {
              width: 100% !important;
              border-collapse: collapse !important;
            }
            th, td {
              border-bottom: 1px solid #ddd !important;
              padding: 5px !important;
              font-size: 8pt !important;
            }
          }
        `}
      </style>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label="Filter By"
              value={filters.filterMode}
              onChange={(e) => setFilters({ ...filters, filterMode: e.target.value })}
            >
              <MenuItem value="dateRange">Date Range</MenuItem>
              <MenuItem value="month">Fee Month / Voucher</MenuItem>
            </TextField>
          </Grid>

          {filters.filterMode === 'dateRange' ? (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Date From"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Date To"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                type="month"
                label="Fee Month & Year"
                value={filters.monthYear}
                onChange={(e) => setFilters({ ...filters, monthYear: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Bank Account"
              value={filters.bankAccount}
              onChange={(e) => setFilters({ ...filters, bankAccount: e.target.value })}
              InputLabelProps={{
                shrink: true
              }}
              SelectProps={{
                displayEmpty: true
              }}
            >
              <MenuItem value="">All Bank Accounts</MenuItem>
              {bankAccounts.map((acc) => (
                <MenuItem key={acc._id} value={acc._id}>
                  {acc.bankName} - {acc.accountNumber}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Status"
              value={filters.statusFilter}
              onChange={(e) => setFilters({ ...filters, statusFilter: e.target.value })}
              InputLabelProps={{
                shrink: true
              }}
              SelectProps={{
                displayEmpty: true
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="reconciled">Reconciled</MenuItem>
              <MenuItem value="suspense">Suspense</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Search />}
              onClick={handleFetchReport}
              sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5a6fd6' }, height: '40px' }}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Metric Cards */}
      {data.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
          <Grid item xs={12} sm={3}>
            <Card sx={{ bgcolor: '#eef2ff', borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Total Transactions</Typography>
                <Typography variant="h6" fontWeight="bold">{data.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card sx={{ bgcolor: '#f0fdf4', borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Reconciled Amount</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  PKR {totalReconciled.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card sx={{ bgcolor: '#fffbe6', borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Suspense Amount</Typography>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  PKR {totalSuspense.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card sx={{ bgcolor: '#f8fafc', borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  PKR {totalAmount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : data.length > 0 ? (
        <Box id="report-print-area">
          <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
            <Button variant="outlined" color="primary" startIcon={<Print />} onClick={handlePrint} sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
              PRINT
            </Button>
            <Button variant="outlined" startIcon={<FileDownload />} onClick={handleExportExcel} color="success" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
              EXPORT
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 1 }}>
            {institution?.logo && (
              <Box
                component="img"
                src={getLogoUrl(institution.logo, institution._id)}
                sx={{ height: 60, width: 60, borderRadius: '50%', mb: 1, objectFit: 'cover' }}
              />
            )}
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontFamily: 'serif' }}>
              {institution?.name || 'TIGES - TAJ CAMPUS'}
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: 'serif', mt: -1 }}>
              {institution?.address?.city || 'Islamabad'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', textDecoration: 'underline', mt: 0.5 }}>
              Bank Transactions & Statement Report
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, px: 1, fontSize: '0.85rem' }}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {filters.filterMode === 'dateRange' ? (
                <>
                  <Typography variant="body2"><b>From:</b> {formatDate(filters.dateFrom)}</Typography>
                  <Typography variant="body2"><b>To:</b> {formatDate(filters.dateTo)}</Typography>
                </>
              ) : (
                <Typography variant="body2"><b>Fee Month:</b> {formatMonthYear(...Object.values(parseMonthYear(filters.monthYear)))}</Typography>
              )}
              <Typography variant="body2">
                <b>Bank Account:</b> {filters.bankAccount ? bankAccounts.find(b => b._id === filters.bankAccount)?.bankName + ' (' + bankAccounts.find(b => b._id === filters.bankAccount)?.accountNumber + ')' : 'All Accounts'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2">
                <b>Print Date:</b> {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} &nbsp; {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderBottomWidth: 2, borderColor: 'black', mb: 1 }} />

          <TableContainer component={Paper} elevation={0} sx={{ border: 'none' }}>
            <Table size="small" sx={{ 
              '& th': { fontWeight: 'bold', borderBottom: '1px solid black', px: 1, py: 0.8, fontSize: '0.8rem' },
              '& td': { borderBottom: '1px solid #eee', px: 1, py: 0.6, fontSize: '0.8rem' }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '50px' }}>Sr #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Campus</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Transection Month</TableCell>
                  <TableCell align="right">Amount (PKR)</TableCell>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details / Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={row._id + '-' + index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1e293b' }}>{row.campusCode}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.date)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: '#7c3aed', fontWeight: 600 }}>{row.feeMonth || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {row.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{row.transactionId || '-'}</TableCell>
                    <TableCell>{getBankDisplay(row)}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.statusLabel}
                        size="small"
                        color={
                          row.status === 'reconciled'
                            ? 'success'
                            : row.status === 'reconciled_suspense'
                            ? 'info'
                            : 'warning'
                        }
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '22px' }}
                      />
                    </TableCell>
                    <TableCell>{row.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ borderTop: '1px solid black', mt: 2, pt: 1, px: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Total Transactions: {data.length}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Total Amount: PKR {totalAmount.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }} className="no-print">
          No bank transactions found for the selected bank or date criteria. Click Search to load transactions.
        </Alert>
      )}
    </Box>
  );
};

export default BankStatementReport;
